import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { resolvePieSlices, resolvePieSliceKeys } from './pie-form.helpers';
import type { ResolvedPieSlice } from './pie-form.helpers';
import { useAnimateAfterDataChange } from '~styles/use-animate-after-data-change';
import { MOTION_DURATION_MS, MOTION_EASE_RECHARTS } from '~styles/motion';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import type { KpiCardData, KpiFormProps } from '../types';
import style from './pie-form.module.scss';

type PieData = Extract<KpiCardData, { form: 'pie' }>;

const PieLegendItem = ({ slice }: { slice: ResolvedPieSlice }) => {
    const tweenedValue = useTweenedValue(slice.value);
    const tweenedPct = useTweenedValue(slice.pct);

    return (
        <li className={style.pie_legend_item}>
            <span
                className={style.pie_legend_dot}
                style={{ background: slice.color }}
            />
            <span className={style.pie_legend_label}>
                {slice.label} -{' '}
                <strong>
                    {Math.round(tweenedValue).toLocaleString('en-US')}
                </strong>{' '}
                ({Math.round(tweenedPct)}%)
            </span>
        </li>
    );
};

const PieHeadline = ({ value, left }: { value: number; left: boolean }) => {
    const tweenedValue = useTweenedValue(value);

    return (
        <div
            className={`${style.pie_headline} h3_medium ${
                left ? style.pie_headline_left : ''
            }`}
        >
            {Math.round(tweenedValue).toLocaleString('en-US')}
        </div>
    );
};

/** A stacked ring runs narrower than a side-by-side one, so it shrinks to fit
 * above the legend and (where requested) the headline; a compact one shrinks
 * further to leave room for the legend in a quarter-width card. Matched by
 * `.pie_chart_stacked` and `.pie_chart_compact` in the stylesheet: change one
 * and change the other. */
const RING_PX = { stacked: 140, side_by_side: 180, compact: 100 } as const;

const PieCard = ({
    data,
    headlineFromFirstSlice,
    legendBelow,
    legendBeside,
}: { data: PieData } & Pick<
    KpiFormProps,
    'headlineFromFirstSlice' | 'legendBelow' | 'legendBeside'
>) => {
    const animate = useAnimateAfterDataChange(
        data.slices.map((slice) => `${slice.label}:${slice.value}`).join('|')
    );
    const slices = resolvePieSlices(data.slices, data.palette);
    const keys = resolvePieSliceKeys(slices);
    const stacked = legendBelow === true;
    const compact = legendBeside === 'compact' && !stacked;
    let ringPx: number = RING_PX.side_by_side;
    if (stacked) ringPx = RING_PX.stacked;
    else if (compact) ringPx = RING_PX.compact;
    const headline = headlineFromFirstSlice ? data.slices[0]?.value : undefined;

    return (
        <div className={style.pie_card}>
            {headline !== undefined && (
                <PieHeadline value={headline} left={stacked} />
            )}
            <div
                className={`${style.pie_form} ${
                    stacked ? style.pie_form_stacked : ''
                } ${compact ? style.pie_form_compact : ''}`}
            >
                <div
                    className={`${style.pie_chart} ${
                        stacked ? style.pie_chart_stacked : ''
                    } ${compact ? style.pie_chart_compact : ''}`}
                >
                    <ResponsiveContainer width="100%" height={ringPx}>
                        <PieChart>
                            <Pie
                                data={slices}
                                dataKey="value"
                                nameKey="label"
                                innerRadius={stacked ? '78%' : '62%'}
                                outerRadius="100%"
                                paddingAngle={1}
                                stroke="none"
                                startAngle={90}
                                endAngle={-270}
                                isAnimationActive={animate}
                                animationDuration={MOTION_DURATION_MS}
                                animationEasing={MOTION_EASE_RECHARTS}
                                animationBegin={0}
                            >
                                {slices.map((slice, index) => (
                                    <Cell
                                        key={keys[index]}
                                        fill={slice.color}
                                    />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <ul className={style.pie_legend}>
                    {slices.map((slice, index) => (
                        <PieLegendItem key={keys[index]} slice={slice} />
                    ))}
                </ul>
            </div>
        </div>
    );
};

/**
 * Pie form: a donut with its legend beside it (colored dot + `label - value
 * (NN%)`), stacking beneath only on mobile, unless `legendBelow` is set, which
 * stacks it (ring above legend) at every viewport regardless of `size`. A card
 * opts in explicitly, so an `sm` pie that never asked for it (the RPP
 * dashboard's breakdown donuts) keeps the side-by-side layout. Slices fill the
 * full ring proportional to their sum; a slice labeled "Other" renders gray.
 * Colors, values, and percentages come from `resolvePieSlices`. An axis
 * carrying no rows at all never reaches here: `hasNoRows` renders the card's
 * empty note instead.
 *
 * The `ResponsiveContainer` height in `PieCard` is not free-floating: it matches
 * `RING_PX`, which the stylesheet's `.pie_chart`/`.pie_chart_stacked` pair
 * also pins the container's width/height to, so the ring stays circular.
 */
export const PieForm = ({
    data,
    headlineFromFirstSlice,
    legendBelow,
    legendBeside,
}: KpiFormProps) => {
    if (data.form !== 'pie') return null;
    return (
        <PieCard
            data={data}
            headlineFromFirstSlice={headlineFromFirstSlice}
            legendBelow={legendBelow}
            legendBeside={legendBeside}
        />
    );
};
